<template>
    <div class="product-header">
        <div class="product-header__breadcrumbs">
            <BaseBreadcrumps :pages="pages" />
        </div>
        <div class="product-header__main">
            <div class="product-header__logo">
                <NuxtImg
                    v-if="product?.id"
                    :src="productLogo"
                    :title="product.displayName"
                    :alt="`${product.displayName} logo`"
                    format="webp"
                    loading="lazy"
                />
            </div>
            <div class="product-header__info">
                <h1 class="product-header__title">{{ product?.displayName }}</h1>
                <span id="pop-up-reviews" class="sr-only">{{ t('testimonials.reviews') }}</span>
                <div id="feefologohere" :[feefoConfig.attributeType]="feefoConfig.productSKU"></div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { IL4ProductHeader } from '~/../types/components/L4ProductHeader';
import type { IL4Airport } from '~/../types/components/L4Airport';
import type { IL4ApiProductFull } from '~/../types/components/L4Products';

defineProps<{ blok: IL4ProductHeader }>();
const product = inject<Ref<IL4ApiProductFull>>('apiProduct');
const airport = inject<Ref<IL4Airport>>('airport');

//TODO get rid of hardcoding url?
const productLogo = computed(() => `https://cdn2.looking4.com/media/images/products/${product?.value?.id}.jpg`);

// Detect language and select correct script to add
const { locale, t } = useI18n();

const { url } = useFeefo();

useHead({
    script: [
        {
            src: url,
            type: 'text/javascript',
            defer: true,
            key: 'feefo-script-product',
        },
    ],
});

const route = useRoute();
const { type: productType, country: productLocation, product_id: productId } = route.params;

const pageType: string = Object.entries(paths).find(([, value]: [string, Record<string, string>]) => {
    return Object.values(value).some((v) => v === productType);
})?.[0];
const capitalizeFirstLetter = (str: string) =>
    str
        .split(' ')
        .map((subStr) => subStr.charAt(0).toUpperCase() + subStr.slice(1))
        .join(' ');
const pages = [
    { href: `/${locale.value}`, text: t('home') },
    {
        href: `/${locale.value}/${productType}`,
        text: t(`breadcrumbs.${pageType}`) ?? t('breadcrumbs.airport-parking'),
    },
    {
        href: `/${locale.value}/${productType}/${productLocation}`,
        text: airport?.name ?? capitalizeFirstLetter(productLocation?.split('-')?.slice(0, -1)?.join(' ')),
    },
    { text: product?.value?.displayName },
];

// Feefo
const propelProductSKU = inject<string>('propelProductSKU', '');
const feefoConfig = computed(() => ({
    productSKU: propelProductSKU ? propelProductSKU : productId,
    attributeType: propelProductSKU ? 'data-parent-product-sku' : 'data-product-sku',
}));
</script>

<style lang="postcss" scoped>
.product-header {
    @apply mb-12 mt-6;
    &__breadcrumbs {
        @apply mb-4;
    }
    &__logo {
        @apply w-[220px] md:w-[30%];
        & > img {
            @apply w-full;
        }
    }
    &__main {
        @apply flex flex-col md:flex-row items-center md:items-start text-center md:text-left gap-6;
    }
    &__title {
        @apply text-2xl text-primary leading-7 mb-2;
    }
}
</style>
